/**
 * @fileoverview The top of Plants as the one to-do list says it (D51;
 * owner, 2026-10-15: "a good design shouldnt say hide the plants"): the same
 * job cards as Today, plant jobs only, the whole week, by day, under
 * "Your plants this week · 5 jobs · 19 plants". Three at most, then "2 more
 * jobs", which shows the rest in place. Garden chores (the tools, the
 * faucets) stay on Today: Plants no longer carries a line for them (C226;
 * owner, 2026-11-03).
 *
 * A tap on a job shows just its plants' cards below, with the bar "Showing
 * the 9 to cover · Show all ×" (the ?plants= filter Today's links use); the
 * circle ticks it, as on Today.
 *
 * Every word is the server's (the brief's todo).
 *
 * Where: GardenConditionsBar, under the weather, when the brief has the list.
 * API: none of its own; marks through useTodoMarks (PlantGrid's).
 * Words (en/es): todo.jobs (the list's label); the rest are the brief's.
 *
 * @module PlantJobs
 */

import React from 'react';
import { X } from 'lucide-react';
import { useTranslation } from '@/shared/hooks/useTranslation';
import type { BriefTodo, TodoJob } from '@/shared/api/todayBrief';
import { JobCard } from '@/features/today/components/JobCard';
import { AnimatedList } from '@/shared/components/ui/AnimatedList';
import type { TodoMarks } from '@/features/today/lib/useTodoMarks';

/** What the top of Plants shows. */
interface PlantJobsProps {
  todo: BriefTodo;
  marks: TodoMarks;
  /** The job the garden below is narrowed to, or null. */
  activeKey: string | null;
  /** Narrows the garden to a job's plants, or back to all of it when it is already the one. */
  onSelect: (job: TodoJob) => void;
  /** Back to the whole garden. */
  onShowAll: () => void;
}

/**
 * The plant jobs.
 *
 * @param props - PlantJobsProps.
 */
export const PlantJobs: React.FC<PlantJobsProps> = ({ todo, marks, activeKey, onSelect, onShowAll }) => {
  const { reactTranslate: t } = useTranslation();
  const [all, setAll] = React.useState(false);
  const byKey = new Map(todo.jobs.map((job) => [job.key, job]));
  const jobs = todo.plants.keys.map((key) => byKey.get(key)).filter((job): job is TodoJob => job !== undefined);
  const active = activeKey ? byKey.get(activeKey) ?? null : null;
  // The one the garden is narrowed to is never hidden behind "more".
  const showAll = all || jobs.slice(todo.shown).some((job) => job.key === activeKey);
  const shown = showAll ? jobs : jobs.slice(0, todo.shown);
  if (jobs.length === 0) return null;

  return (
    <section aria-labelledby="plants-jobs" className="mb-4">
      <h2 id="plants-jobs" className="mb-2 flex items-baseline justify-between gap-2 px-1 text-xl font-bold leading-tight font-body">
        {todo.plants.title}
        {todo.plants.count && <span className="font-sans text-sm font-normal text-muted-foreground">{todo.plants.count}</span>}
      </h2>
      {jobs.length > 0 && (
        // Folds a job away and opens a new one smoothly (AnimatedList; owner, 2026-10-16).
        <AnimatedList
          className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3"
          aria-label={t('todo.jobs')}
          items={shown.map((job) => ({
            key: job.key,
            node: (
              <JobCard
                job={job}
                words={todo.words}
                pending={marks.pending[job.key]}
                busy={marks.busyKey === job.key}
                failedWords={marks.failed?.key === job.key ? marks.failed.words : null}
                compact
                selected={activeKey === job.key}
                onSelect={job.filter ? () => onSelect(job) : undefined}
                onMark={(mark) => { void marks.mark(job, mark); }}
              />
            ),
          }))}
        />
      )}
      {todo.plants.more && (
        <button
          type="button"
          onClick={() => setAll((current) => !current)}
          className="block w-full py-2.5 text-center text-[0.9375rem] font-semibold text-primary underline-offset-4 hover:underline"
        >
          {showAll ? todo.words.fewer : todo.plants.more}
        </button>
      )}
      {/* The garden below is one job's plants: say so, with the way back. */}
      {active?.filter && (
        <div className="mt-2 flex items-center justify-between gap-2 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground">
          <span>{active.filter.text}</span>
          <button type="button" onClick={onShowAll} className="inline-flex min-h-9 items-center gap-1 underline-offset-4 hover:underline">
            {todo.words.showAll}
            <X className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </div>
      )}
    </section>
  );
};
